import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import attendanceApi from '../../api/attendanceApi';
import { SetAlertAndClose } from './AlertReducer';
import { AttendanceStatusEnum, LoadingStatusEnum } from '../../constants/enums';

// ----------------------------------------------------------------------------

export const CreateAttendance = createAsyncThunk<any, any>('attendance/createAttendance', async (data, thunkAPI) => {
  const chosenDay = dayjs(data.currentDay).format('YYYY-MM-DD');
  data.currentDay = chosenDay;
  const res = await attendanceApi.create(data);

  if (res.status === 200) {
    thunkAPI.dispatch(SetAlertAndClose({ data: res.data }));
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const EditAttendance = createAsyncThunk<any, { data: any; id: string }>(
  'attendance/editAttendance',
  async ({ data, id }, thunkAPI) => {
    const res = await attendanceApi.update(data, id);

    if (res.status === 200) {
      thunkAPI.dispatch(SetAlertAndClose({ data: res.data }));
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const SetPendingStatus = createAsyncThunk<any, string>('attendance/setPendingStatus', async (id, thunkAPI) => {
  const res = await attendanceApi.setPendingStatus(id);

  if (res.status === 200) {
    return id;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetAttendancesByWeeks = createAsyncThunk('attendance/getAttendancesByWeeks', async (__, thunkAPI) => {
  const res = await attendanceApi.getByWeeks();

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const GetAttendancesByMonth = createAsyncThunk<any, any>(
  'attendance/getAttendancesByMonth',
  async (date, thunkAPI) => {
    const dayjsDate = dayjs(date);

    const year = dayjsDate.year();
    const month = dayjsDate.month();

    const res = await attendanceApi.getByMonth({
      year,
      month,
    });

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetAttendanceFormData = createAsyncThunk<any, any>(
  'attendance/getAttendanceFormData',
  async (params, thunkAPI) => {
    const chosenDay = dayjs(params.date).format('YYYY-MM-DD');
    params.date = chosenDay;

    const res = await attendanceApi.getForm(params);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

// state
const initialState = {
  payrolls: [],
  attendances: [],
  formData: {
    employeeAttendances: [],
    currentDay: null,
    status: null,
    _id: null,
  },
  loading: 'idle',
};

// slice
export const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    ClearAttendance: () => initialState,
  },
  extraReducers: (builder) => {
    // ------------------CreateAttendance-------------------------
    builder.addCase(CreateAttendance.pending, (state) => ({
      ...state,
      loading: 'pending',
    }));

    builder.addCase(CreateAttendance.fulfilled, (state) => ({
      ...state,
      // formData: {
      // 	...state.formData,
      // 	...action.payload.data,
      // },
      // loading: 'succeeded',
      loading: 'pending',
    }));

    builder.addCase(CreateAttendance.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));

    // ------------------EditAttendance-------------------------
    builder.addCase(EditAttendance.pending, (state) => ({
      ...state,
      loading: 'pending',
    }));

    builder.addCase(EditAttendance.fulfilled, (state, action) => ({
      ...state,
      formData: action.payload.data,
      loading: 'succeeded',
    }));

    builder.addCase(EditAttendance.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));

    // ------------------SetPendingStatus-------------------------
    builder.addCase(SetPendingStatus.pending, (state) => ({
      ...state,
      loading: 'pending',
    }));

    builder.addCase(SetPendingStatus.fulfilled, (state: any, action) => ({
      ...state,
      formData: {
        ...state.formData,
        status: AttendanceStatusEnum.Pending,
      },
      payrolls: state.payrolls.map((el: any) => {
        if (el._id === action.payload) {
          return {
            ...el,
            status: AttendanceStatusEnum.Pending,
          };
        }

        return el;
      }),
      loading: 'succeeded',
    }));

    builder.addCase(SetPendingStatus.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));

    // ------------------GetAttendanceFormData-------------------------
    builder.addCase(GetAttendanceFormData.pending, (state) => ({
      ...state,
      loading: 'pending',
    }));

    builder.addCase(GetAttendanceFormData.fulfilled, (state, action) => ({
      ...state,
      formData: action.payload.data,
      loading: 'succeeded',
    }));

    builder.addCase(GetAttendanceFormData.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));

    // ------------------GetAttendancesByWeeks-------------------------
    builder.addCase(GetAttendancesByWeeks.pending, (state) => ({
      ...state,
      loading: 'pending',
    }));

    builder.addCase(GetAttendancesByWeeks.fulfilled, (state, action) => ({
      ...state,
      attendances: action.payload.data.list,
      loading: 'succeeded',
    }));

    builder.addCase(GetAttendancesByWeeks.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));

    // ------------------GetAttendancesByMonth-------------------------
    builder.addCase(GetAttendancesByMonth.pending, (state) => ({
      ...state,
      loading: 'pending',
    }));

    builder.addCase(GetAttendancesByMonth.fulfilled, (state, action) => ({
      ...state,
      ...action.payload.data,
      loading: 'succeeded',
    }));

    builder.addCase(GetAttendancesByMonth.rejected, (state) => ({
      ...state,
      loading: 'failed',
    }));

    // ------------------GetPayrolls-------------------------
    // builder.addCase(GetPayrolls.pending, (state) => ({
    //   ...state,
    //   payrollsLoading: 'pending',
    // }));

    // builder.addCase(GetPayrolls.fulfilled, (state, action) => ({
    //   ...state,
    //   payrolls: action.payload.data.list,
    //   payrollsLoading: 'succeeded',
    // }));

    // builder.addCase(GetPayrolls.rejected, (state) => ({
    //   ...state,
    //   payrollsLoading: 'failed',
    // }));
  },
});

// Export Actions
export const { ClearAttendance } = attendanceSlice.actions;

// Export Selectors
export const selectAttendanceFormData = (state) => state.attendance.formData;
export const selectAttendanceList = (state) => state.attendance.attendances;
export const selectAttendancePayrolls = (state) => state.attendance.payrolls;
export const selectAttendanceLoading = (state) => state.attendance.loading;
export const selectAttendanceIsLoading = (state) => state.attendance.loading === LoadingStatusEnum.Pending;

// export const selectAttendancePayrolls = (state) => state.attendance.payrolls;
// export const selectAttendancePayrollsLoading = (state) => state.attendance.payrollsLoading;

export default attendanceSlice.reducer;
